import { FC } from 'react';
import { Box, Container, Typography, Button, Paper, Grid } from '@mui/material';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import SpeedIcon from '@mui/icons-material/Speed';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import { ExcelsusLogo } from '@carcareers/assets';

export const ExcelsusBanner: FC = () => {
  return (
    <Box sx={{ bgcolor: '#f8f9fa', py: { xs: 6, md: 8 } }}>
      <Container maxWidth="lg">
        <Paper
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: 3,
            bgcolor: '#231f20',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Grid container spacing={4} alignItems="center">
            {/* Logo and Introduction */}
            <Grid item xs={12} md={5}>
              <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
                <Box
                  sx={{
                    mb: 3,
                    display: 'flex',
                    justifyContent: { xs: 'center', md: 'flex-start' },
                    '& svg': { width: '200px', height: 'auto', maxWidth: '100%' },
                  }}
                >
                  <ExcelsusLogo />
                </Box>
                <Typography
                  variant="h6"
                  sx={{
                    color: '#ce222f',
                    fontWeight: 700,
                    mb: 1,
                    fontSize: { xs: '0.9rem', md: '1rem' },
                  }}
                >
                  Speed, Accuracy, Consistency
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    color: 'white',
                    fontWeight: 700,
                    mb: 2,
                    fontSize: { xs: '1.5rem', md: '2rem' },
                  }}
                >
                  Background Checks for Less!
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: '#e0e0e0',
                    mb: 3,
                    fontSize: { xs: '0.95rem', md: '1.05rem' },
                    lineHeight: 1.6,
                  }}
                >
                  Excelsus Data provides employment screening, including national and county criminal checks, motor
                  vehicle reports, and drug testing at a fraction of traditional costs.
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  component="a"
                  href="https://www.excelsusdata.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    bgcolor: '#ce222f',
                    color: 'white',
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    boxShadow: 3,
                    '&:hover': {
                      bgcolor: '#a51b27',
                      boxShadow: 6,
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  Learn More
                </Button>
              </Box>
            </Grid>

            {/* Features */}
            <Grid item xs={12} md={7}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={4}>
                  <Box
                    sx={{
                      textAlign: 'center',
                      p: 2,
                      bgcolor: 'rgba(255, 255, 255, 0.05)',
                      borderRadius: 2,
                      height: '100%',
                    }}
                  >
                    <LocalOfferIcon
                      sx={{
                        fontSize: 48,
                        color: '#ce222f',
                        mb: 1.5,
                      }}
                    />
                    <Typography
                      variant="h3"
                      sx={{
                        color: '#ce222f',
                        fontWeight: 700,
                        mb: 0.5,
                        fontSize: '1.75rem',
                      }}
                    >
                      50%
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'white',
                        fontWeight: 600,
                      }}
                    >
                      Cost Reduction
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Box
                    sx={{
                      textAlign: 'center',
                      p: 2,
                      bgcolor: 'rgba(255, 255, 255, 0.05)',
                      borderRadius: 2,
                      height: '100%',
                    }}
                  >
                    <SpeedIcon
                      sx={{
                        fontSize: 48,
                        color: '#ce222f',
                        mb: 1.5,
                      }}
                    />
                    <Typography
                      variant="h3"
                      sx={{
                        color: '#ce222f',
                        fontWeight: 700,
                        mb: 0.5,
                        fontSize: '1.75rem',
                      }}
                    >
                      10x
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'white',
                        fontWeight: 600,
                      }}
                    >
                      Faster Processing
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Box
                    sx={{
                      textAlign: 'center',
                      p: 2,
                      bgcolor: 'rgba(255, 255, 255, 0.05)',
                      borderRadius: 2,
                      height: '100%',
                    }}
                  >
                    <VerifiedUserIcon
                      sx={{
                        fontSize: 48,
                        color: '#ce222f',
                        mb: 1.5,
                      }}
                    />
                    <Typography
                      variant="h3"
                      sx={{
                        color: '#ce222f',
                        fontWeight: 700,
                        mb: 0.5,
                        fontSize: '1.75rem',
                      }}
                    >
                      99.9%
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'white',
                        fontWeight: 600,
                      }}
                    >
                      Accuracy Rate
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Box
                    sx={{
                      p: 2.5,
                      borderRadius: 2,
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      sx={{
                        color: '#ce222f',
                        fontWeight: 700,
                        mb: 1,
                        fontSize: '1rem',
                      }}
                    >
                      Comprehensive Screening Services
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#e0e0e0',
                        lineHeight: 1.6,
                      }}
                    >
                      • National &amp; County Criminal Checks • Motor Vehicle Reports • Drug &amp; Alcohol Testing •
                      Employment Verification • Education Verification • Identity Verification • Social Security Traces
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Paper>
      </Container>
    </Box>
  );
};
